import { useState, useEffect } from "react";

interface AccessibilitySettings {
  fontSize: number;
  highContrast: boolean;
  grayscale: boolean;
  underlineLinks: boolean;
  hideImages: boolean;
  lineHeight: number;
  letterSpacing: number;
  cursorLarge: boolean;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 100,
  highContrast: false,
  grayscale: false,
  underlineLinks: false,
  hideImages: false,
  lineHeight: 1,
  letterSpacing: 0,
  cursorLarge: false,
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);

  useEffect(() => {
    const saved = localStorage.getItem("a11y-settings");
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch {
        setSettings(defaultSettings);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("a11y-settings", JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  const applySettings = (s: AccessibilitySettings) => {
    const root = document.documentElement;
    root.style.fontSize = `${s.fontSize}%`;

    const body = document.body;
    body.style.filter = s.grayscale ? "grayscale(100%)" : "";

    if (s.highContrast) {
      body.classList.add("a11y-high-contrast");
    } else {
      body.classList.remove("a11y-high-contrast");
    }

    if (s.underlineLinks) {
      body.classList.add("a11y-underline-links");
    } else {
      body.classList.remove("a11y-underline-links");
    }

    if (s.hideImages) {
      body.classList.add("a11y-hide-images");
    } else {
      body.classList.remove("a11y-hide-images");
    }

    if (s.cursorLarge) {
      body.classList.add("a11y-large-cursor");
    } else {
      body.classList.remove("a11y-large-cursor");
    }

    body.style.lineHeight = s.lineHeight > 1 ? `${s.lineHeight}` : "";
    body.style.letterSpacing = s.letterSpacing > 0 ? `${s.letterSpacing}px` : "";
  };

  const update = (key: keyof AccessibilitySettings, value: boolean | number) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const reset = () => {
    setSettings(defaultSettings);
    document.documentElement.style.fontSize = "";
    document.body.style.filter = "";
    document.body.style.lineHeight = "";
    document.body.style.letterSpacing = "";
    document.body.classList.remove("a11y-high-contrast", "a11y-underline-links", "a11y-hide-images", "a11y-large-cursor");
    localStorage.removeItem("a11y-settings");
  };

  const isDefault = JSON.stringify(settings) === JSON.stringify(defaultSettings);

  return (
    <>
      {/* Global styles for accessibility classes */}
      <style>{`
        .a11y-high-contrast { background: #000 !important; color: #fff !important; }
        .a11y-high-contrast * { background-color: #000 !important; color: #fff !important; border-color: #fff !important; }
        .a11y-high-contrast a { color: #ff0 !important; }
        .a11y-underline-links a { text-decoration: underline !important; }
        .a11y-hide-images img { visibility: hidden !important; }
        .a11y-large-cursor, .a11y-large-cursor * { cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cpath d='M8 2L8 26L14 20L18 30L21 29L17 19L25 19Z' fill='black' stroke='white' stroke-width='1.5'/%3E%3C/svg%3E") 0 0, auto !important; }
      `}</style>

      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Accessibility Options"
        className="fixed bottom-6 left-6 z-50 w-12 h-12 flex items-center justify-center bg-brand-dark text-white rounded-full cursor-pointer transition-all hover:bg-brand-green"
        style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}
      >
        <i className="ri-eye-line text-xl"></i>
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 left-6 z-50 bg-white rounded-2xl w-80 overflow-hidden"
          style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}
          role="dialog"
          aria-label="Accessibility Adjustments"
        >
          {/* Header */}
          <div className="bg-brand-dark px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-eye-line text-brand-green text-lg"></i>
              <span className="font-heading font-bold text-white text-sm">Accessibility Adjustments</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white cursor-pointer transition-colors"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <div className="p-4 flex flex-col gap-3 max-h-[70vh] overflow-y-auto">

            {/* Font Size */}
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-heading font-semibold text-brand-dark text-xs mb-2">Text Size</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => update("fontSize", Math.max(80, settings.fontSize - 10))}
                  className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-brand-dark hover:border-brand-green hover:text-brand-green cursor-pointer transition-all font-bold text-sm"
                  aria-label="Decrease font size"
                >A-</button>
                <div className="flex-1 text-center font-body text-xs text-gray-500">{settings.fontSize}%</div>
                <button
                  onClick={() => update("fontSize", Math.min(150, settings.fontSize + 10))}
                  className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-brand-dark hover:border-brand-green hover:text-brand-green cursor-pointer transition-all font-bold text-sm"
                  aria-label="Increase font size"
                >A+</button>
              </div>
            </div>

            {/* Line Height */}
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-heading font-semibold text-brand-dark text-xs mb-2">Line Height</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => update("lineHeight", Math.max(1, settings.lineHeight - 0.5))}
                  className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-brand-dark hover:border-brand-green hover:text-brand-green cursor-pointer transition-all"
                  aria-label="Decrease line height"
                ><i className="ri-subtract-line text-sm"></i></button>
                <div className="flex-1 text-center font-body text-xs text-gray-500">
                  {settings.lineHeight === 1 ? "Default" : `${settings.lineHeight}x`}
                </div>
                <button
                  onClick={() => update("lineHeight", Math.min(3, settings.lineHeight + 0.5))}
                  className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-brand-dark hover:border-brand-green hover:text-brand-green cursor-pointer transition-all"
                  aria-label="Increase line height"
                ><i className="ri-add-line text-sm"></i></button>
              </div>
            </div>

            {/* Letter Spacing */}
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-heading font-semibold text-brand-dark text-xs mb-2">Letter Spacing</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => update("letterSpacing", Math.max(0, settings.letterSpacing - 1))}
                  className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-brand-dark hover:border-brand-green hover:text-brand-green cursor-pointer transition-all"
                  aria-label="Decrease letter spacing"
                ><i className="ri-subtract-line text-sm"></i></button>
                <div className="flex-1 text-center font-body text-xs text-gray-500">
                  {settings.letterSpacing === 0 ? "Default" : `+${settings.letterSpacing}px`}
                </div>
                <button
                  onClick={() => update("letterSpacing", Math.min(5, settings.letterSpacing + 1))}
                  className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg text-brand-dark hover:border-brand-green hover:text-brand-green cursor-pointer transition-all"
                  aria-label="Increase letter spacing"
                ><i className="ri-add-line text-sm"></i></button>
              </div>
            </div>

            {/* Toggle Options */}
            {[
              { key: "highContrast" as const, label: "High Contrast", icon: "ri-contrast-2-line" },
              { key: "grayscale" as const, label: "Grayscale", icon: "ri-contrast-drop-line" },
              { key: "underlineLinks" as const, label: "Underline Links", icon: "ri-link-m" },
              { key: "hideImages" as const, label: "Hide Images", icon: "ri-image-2-line" },
              { key: "cursorLarge" as const, label: "Large Cursor", icon: "ri-cursor-line" },
            ].map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => update(key, !settings[key])}
                className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all cursor-pointer w-full text-left ${
                  settings[key]
                    ? "bg-brand-green/10 border-brand-green text-brand-green"
                    : "bg-gray-50 border-gray-200 text-brand-dark hover:border-brand-green/40"
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className={`${icon} text-base`}></i>
                  <span className="font-heading font-semibold text-xs">{label}</span>
                </div>
                <div className={`w-9 h-5 rounded-full transition-all relative ${settings[key] ? "bg-brand-green" : "bg-gray-300"}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${settings[key] ? "left-4" : "left-0.5"}`} />
                </div>
              </button>
            ))}

            {/* Reset */}
            {!isDefault && (
              <button
                onClick={reset}
                className="w-full py-2.5 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 font-heading font-semibold text-xs transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-refresh-line"></i>
                Reset All Settings
              </button>
            )}

          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-100 text-center">
            <p className="font-body text-xs text-gray-400">Settings are saved automatically</p>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
